import { first, last, maxby, meanby, minby } from '@quromedical/utils';

import type { Measurement } from './internal';

export type Window = {
  start: number;
  end: number;
  measurements: Measurement[];
};

export type Range = Window & {
  mean?: number;
  min?: Measurement;
  max?: Measurement;
};

/**
 * Aggregate measurement over a set of ranges
 *
 * @param window timeframe for the results to be calculated in
 * @param points measurements at points in time
 * @returns the measurements and the scores that it has (start,end,max,min and mean)
 */
export const getAggregatedRanges = (
  windowSize: number,
  points: Measurement[],
): Range[] =>
  getWindowPoints(windowSize, points).map((item) => ({
    start: item.start,
    end: item.end,
    measurements: item.measurements,
    mean: Math.round(meanby(item.measurements, (value) => +value.value)),
    max: maxby(item.measurements, (value) => value.value),
    min: minby(item.measurements, (value) => value.value),
  }));

type Boundary = {
  start: number;
  end: number;
};

type BoundaryHandler = (
  range: Boundary,
) => (measurement: Measurement) => boolean;

const allInclusive: BoundaryHandler =
  ({ start, end }) =>
  (measurement) =>
    measurement.ts >= start && measurement.ts <= end;

/**
 * Bucket points into groups based on a time window duration
 */
export const getWindowPoints = (
  windowSize: number,
  points: Measurement[],
): Window[] => {
  const sortedPoints = points.sort((a, b) => a.ts - b.ts);
  const firstPoint = first(sortedPoints)?.ts;
  const lastPoint = last(sortedPoints)?.ts;

  if (!(firstPoint && lastPoint)) {
    return [];
  }

  const numberOfWindows = Math.ceil((lastPoint - firstPoint) / windowSize);

  const windows = new Array(numberOfWindows)
    .fill(undefined)
    .map<Window>((_, index) => {
      const start = firstPoint + index * windowSize;
      const end = start + windowSize;

      const measurements = sortedPoints.filter(allInclusive({ start, end }));

      return {
        measurements,
        start,
        end,
      };
    });

  return windows;
};
